/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { isBlank, isNone } from '@ember/utils';

enum RecipientType {
  Id = 'id',
  Email = 'email',
  Custom = 'custom',
}
type Item = {
  label: string;
  value: RecipientType;
};
const RECIPIENT_OPTIONS: Item[] = [
  { label: 'id', value: RecipientType.Id },
  { label: 'email', value: RecipientType.Email },
  { label: 'custom', value: RecipientType.Custom },
];
const RECIPIENT_LIQUID: Partial<Record<RecipientType, string>> = {
  [RecipientType.Id]: '{{customer.id}}',
  [RecipientType.Email]: '{{customer.email}}',
};

interface RecipientArgs {
  recipient: string;
  onChange: (recipient: string) => unknown;
  onRecipientTypeChange: (isCustomizing: boolean) => unknown;
}

export default class Recipient extends Component<RecipientArgs> {
  @service environment!: EnvironmentService;
  @tracked isCustomRecipient = false;
  @tracked isTouched = false;
  @tracked recipient = '';

  constructor(owner: unknown, args: RecipientArgs) {
    super(owner, args);
    this.recipient = args.recipient;
    this.isCustomRecipient = this.getRecipientTypeFromValue(args.recipient) === RecipientType.Custom;
    this.args.onRecipientTypeChange(this.isCustomRecipient);
  }

  get env() {
    return this.environment.current;
  }

  get recipientOptions() {
    return RECIPIENT_OPTIONS.filter(this.isRecipientTypeValidForThisWorkspace);
  }

  @action
  isRecipientTypeValidForThisWorkspace(item: Item) {
    return this.canUseEmail(item) || this.canUseId(item);
  }

  @action
  canUseId(item: Item) {
    return item.value !== RecipientType.Id || this.isIdEnabled;
  }

  get isIdEnabled() {
    return this.env.canIdentifyById;
  }

  @action
  canUseEmail(item: Item) {
    return item.value !== RecipientType.Id || this.isEmailEnabled;
  }

  get isEmailEnabled() {
    return this.env.canIdentifyByEmail;
  }

  get isValid() {
    return !this.isCustomRecipient || !isBlank(this.recipient);
  }

  get errors() {
    return this.isValid ? [] : ["can't be blank"];
  }

  get selectedRecipient() {
    let selected = '';
    if (!this.isCustomRecipient) {
      let { recipient } = this.args;
      let selectedType = this.getRecipientTypeFromValue(recipient);
      if (!isNone(selectedType)) {
        selected = selectedType;
      }
    } else {
      selected = RecipientType.Custom;
    }
    return selected;
  }

  get showErrors() {
    return this.isTouched && !this.isValid;
  }

  @action
  getRecipientTypeFromValue(recipient: string) {
    if (recipient === this.liquidFor(RecipientType.Id)) {
      return this.isIdEnabled ? RecipientType.Id : RecipientType.Custom;
    }
    if (recipient === this.liquidFor(RecipientType.Email)) {
      return this.isEmailEnabled ? RecipientType.Email : RecipientType.Custom;
    }
    return isBlank(recipient) ? null : RecipientType.Custom;
  }

  @action
  onBlur() {
    if (!this.isTouched) {
      this.isTouched = true;
    }
  }

  @action
  setRecipient(item: { label: string; value: string }) {
    let selectedType = item.value as RecipientType;
    if (selectedType === RecipientType.Custom) {
      this.isCustomRecipient = true;
      if (!isBlank(this.recipient)) {
        this.args.onChange(this.recipient);
      }
    } else {
      this.isCustomRecipient = false;
      this.args.onChange(this.liquidFor(selectedType)!);
    }
    this.args.onRecipientTypeChange(this.isCustomRecipient);
  }

  liquidFor(type: RecipientType) {
    return RECIPIENT_LIQUID[type];
  }

  @action
  setCustomRecipient(recipient: string) {
    this.recipient = recipient;
    this.args.onChange(recipient);
  }
}
